import type { ReadingFilter } from "../types/nem12MeterDetail.types";
import type {
  ApiErrorEnvelope,
  ApiEnvelope,
  BatchStatus,
  Nem12ApiError,
  PaginationMetadata,
  SummaryMeter,
  BillingMeter,
  BillingData,
  CursorState,
  SummaryFilterState,
  BillingFilterState,
  MissingFilterState,
  DuplicatesFilterState,
  Nem12ChartData,
} from "../types/nem12Viewer.types";
import { getCSSVariable } from "@/shared/utils/utils";
import { DEFAULT_THEME_COLOR } from "@/shared/constants";
import { OptionValueStringType } from "@/shared/type/type";

const chartColor = DEFAULT_THEME_COLOR;

export const getDynamicPrimaryColor = (): string =>
  getCSSVariable("--primary", chartColor);

export const MAX_FILE_SIZE_BYTES = 100 * 1024 * 1024;
export const MAX_FILE_SIZE_LABEL = `${MAX_FILE_SIZE_BYTES / (1024 * 1024)} MB`;

export const FILE_TYPE_OPTIONS = [
  { label: "NEM12", value: "NEM12" },
  { label: "e-NEM12", value: "e-NEM12", isDisabled: true },
];

const nem12ErrorMessages = new Map<string, string>([
  ["NO_FILES", "Please select at least one file before uploading."],
  [
    "TOO_MANY_FILES",
    "You have selected too many files. Please reduce the number of files.",
  ],
  [
    "FILE_TOO_LARGE",
    `One or more files exceed the ${MAX_FILE_SIZE_LABEL} limit.`,
  ],
  ["BATCH_NOT_FOUND", "Batch not found. It may have expired or is invalid."],
  [
    "ARTIFACT_NOT_FOUND",
    "Requested result is not ready yet. Please try again.",
  ],
  ["NMI_NOT_FOUND", "No matching NMI found for this batch."],
  ["METER_NOT_FOUND", "No matching meter found for this batch."],
  ["FILE_NOT_FOUND", "Validation file was not found for this batch."],
  ["INVALID_PARAM", "One or more query parameters are invalid."],
  ["EXPORT_EMPTY", "No exportable data was found for the requested scope."],
  ["PAYLOAD_TOO_LARGE", "The request body exceeds the allowed size."],
  ["INTERNAL_ERROR", "Unexpected server error occurred. Please try again."],
]);

export const isProcessingStatus = (status?: BatchStatus | null): boolean => {
  return status === "Queued" || status === "Processing";
};

export const getFriendlyErrorMessage = (
  code?: string,
  fallbackMessage?: string,
): string => {
  if (code && nem12ErrorMessages.has(code)) {
    return nem12ErrorMessages.get(code) || fallbackMessage || "";
  }

  if (fallbackMessage) {
    return fallbackMessage;
  }

  return "Something went wrong while processing your request.";
};

export const mapErrorEnvelope = (
  errorEnvelope: ApiErrorEnvelope,
  statusCode?: number,
): Nem12ApiError => {
  const code = errorEnvelope.error?.code || "UNKNOWN_ERROR";
  const message = getFriendlyErrorMessage(code, errorEnvelope.error?.message);

  return {
    statusCode,
    code,
    message,
    details: errorEnvelope.error?.details,
  };
};

export const getPaginationMeta = <TData>(
  envelope?: ApiEnvelope<TData>,
): PaginationMetadata => {
  return envelope?.meta?.pagination ?? {};
};

export const toNumber = (value: unknown): number | null => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

export const toDisplayValue = (value: unknown): string => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  if (typeof value === "object") {
    if ("value" in (value as Record<string, unknown>)) {
      const candidate = (value as Record<string, unknown>).value;
      return candidate === null || candidate === undefined
        ? "-"
        : String(candidate);
    }

    return JSON.stringify(value);
  }

  return String(value);
};

export const formatFileSize = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return "0 B";
  }

  const units = ["B", "KB", "MB", "GB"];
  let unitIndex = 0;
  let value = bytes;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  return `${value.toFixed(unitIndex === 0 ? 0 : 2)} ${units[unitIndex]}`;
};

export const parseContentDispositionFileName = (
  contentDisposition?: string,
  fallback = "nem12_export.zip",
): string => {
  if (!contentDisposition) {
    return fallback;
  }

  const utfMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utfMatch?.[1]) {
    return decodeURIComponent(utfMatch[1]);
  }

  const quotedMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  if (quotedMatch?.[1]) {
    return quotedMatch[1];
  }

  return fallback;
};

export const triggerBlobDownload = (blob: Blob, fileName: string): void => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

export const defaultCursorState = (
  orderBy: string,
  size: number,
): CursorState => ({
  pageIndex: 0,
  pageSize: size,
  cursorByPage: { 0: null },
  nextCursor: null,
  totalItems: 0,
  orderBy: orderBy,
  direction: "asc",
});

export const applyCursorMetaUpdate = (
  prev: CursorState,
  nextCursor: string | null,
  totalItems: number,
): CursorState => {
  let nextMap = prev.cursorByPage;
  let mapChanged = false;

  if (nextCursor && nextMap[prev.pageIndex + 1] === undefined) {
    nextMap = {
      ...prev.cursorByPage,
      [prev.pageIndex + 1]: nextCursor,
    };
    mapChanged = true;
  }

  const nextCursorChanged = prev.nextCursor !== nextCursor;
  const totalItemsChanged = prev.totalItems !== totalItems;

  if (!mapChanged && !nextCursorChanged && !totalItemsChanged) {
    return prev;
  }

  return {
    ...prev,
    cursorByPage: nextMap,
    nextCursor,
    totalItems,
  };
};

export const getMeterNmi = (meter: SummaryMeter | BillingMeter): string => {
  return String(meter.identity?.nmi || "");
};

export const getMeterSerial = (meter: SummaryMeter | BillingMeter): string => {
  return String(meter.identity?.meterSerialNumber || "");
};

export const getMeterPeriodStart = (
  meter: SummaryMeter | BillingMeter,
): string => {
  return formatTimePoint(meter.periodStart);
};

export const getMeterPeriodEnd = (
  meter: SummaryMeter | BillingMeter,
): string => {
  return formatTimePoint(meter.periodEnd);
};

export const getQuantityValue = (value: unknown): number | null => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  if (value && typeof value === "object") {
    const quantityValue = (value as Record<string, unknown>).value;
    return getQuantityValue(quantityValue);
  }

  return null;
};

export const formatTimePoint = (value: unknown): string => {
  const toDisplayTimestamp = (raw: string): string => {
    const matched = raw.match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})/);

    if (!matched) {
      return raw;
    }

    const [, year, month, day, hour, minute] = matched;
    return `${day}-${month}-${year} ${hour}:${minute}`;
  };

  if (!value) {
    return "-";
  }

  if (typeof value === "string") {
    return toDisplayTimestamp(value);
  }

  if (typeof value === "object") {
    const valueObject = value as Record<string, unknown>;
    const nemTime = valueObject.nemTime;
    const utc = valueObject.utc;

    if (typeof nemTime === "string" && nemTime.length > 0) {
      return toDisplayTimestamp(nemTime);
    }

    if (typeof utc === "string" && utc.length > 0) {
      return toDisplayTimestamp(utc);
    }

    const timestamp = valueObject.timestamp;
    if (timestamp && typeof timestamp === "object") {
      return formatTimePoint(timestamp);
    }

    const date = valueObject.date;
    if (typeof date === "string" && date.length > 0) {
      const matched = date.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (matched) {
        const [, year, month, day] = matched;
        return `${day}-${month}-${year}`;
      }

      return date;
    }
  }

  return "-";
};

export const formatNumberDisplay = (value: unknown): string => {
  const parsed = getQuantityValue(value);
  return parsed === null ? "-" : String(parsed);
};

export const getSummaryActiveEnergy = (meter: SummaryMeter): number | null => {
  const record = meter as Record<string, unknown>;
  const activeEnergy = record.activeEnergy as
    | Record<string, unknown>
    | undefined;

  const totalFromActiveEnergy = getQuantityValue(activeEnergy?.total);
  if (totalFromActiveEnergy !== null) {
    return totalFromActiveEnergy;
  }

  const directActiveEnergy = getQuantityValue(record.activeEnergy);
  if (directActiveEnergy !== null) {
    return directActiveEnergy;
  }

  const totals = record.totals as Record<string, unknown> | undefined;
  const totalsE = getQuantityValue(totals?.E);
  if (totalsE !== null) {
    return totalsE;
  }

  const channels = record.channels as Record<string, unknown> | undefined;
  const channelE = getQuantityValue(channels?.E);
  if (channelE !== null) {
    return channelE;
  }

  const calculated = record.calculated as Record<string, unknown> | undefined;
  const calculatedActive = getQuantityValue(calculated?.activeEnergy);
  if (calculatedActive !== null) {
    return calculatedActive;
  }

  return getQuantityValue(record.activeEnergyTotal);
};

export const getTimePointRawValue = (value: unknown): string | null => {
  if (!value) {
    return null;
  }

  if (typeof value === "string") {
    return value;
  }

  if (typeof value === "object") {
    const record = value as Record<string, unknown>;

    if (typeof record.nemTime === "string" && record.nemTime.length > 0) {
      return record.nemTime;
    }

    if (typeof record.utc === "string" && record.utc.length > 0) {
      return record.utc;
    }

    if (typeof record.timestamp === "string" && record.timestamp.length > 0) {
      return record.timestamp;
    }
  }

  return null;
};

export const getDurationHours = (meter: SummaryMeter): number | null => {
  const startRaw = getTimePointRawValue(meter.periodStart);
  const endRaw = getTimePointRawValue(meter.periodEnd);

  if (!startRaw || !endRaw) {
    return null;
  }

  const startMs = Date.parse(startRaw);
  const endMs = Date.parse(endRaw);

  if (
    !Number.isFinite(startMs) ||
    !Number.isFinite(endMs) ||
    endMs <= startMs
  ) {
    return null;
  }

  return (endMs - startMs) / (1000 * 60 * 60);
};

export const getSummaryKWhMetric = (
  meter: SummaryMeter,
  metric: "max" | "min" | "avg",
): number | null => {
  const record = meter as Record<string, unknown>;

  const direct = getQuantityValue(record[`${metric}ActiveEnergy`]);
  if (direct !== null) {
    return direct;
  }

  const activeEnergy = record.activeEnergy as
    | Record<string, unknown>
    | undefined;
  const activeEnergyMetric = getQuantityValue(activeEnergy?.[metric]);
  if (activeEnergyMetric !== null) {
    return activeEnergyMetric;
  }

  const calculated = record.calculated as Record<string, unknown> | undefined;
  const calculatedActiveEnergy = calculated?.activeEnergy as
    | Record<string, unknown>
    | undefined;

  return getQuantityValue(calculatedActiveEnergy?.[metric]);
};

export const getSummaryMaxDemand = (meter: SummaryMeter): number | null => {
  const record = meter as Record<string, unknown>;
  const apparentDemand = record.apparentDemand;

  if (typeof apparentDemand === "number") {
    return Number.isFinite(apparentDemand) ? apparentDemand : null;
  }

  if (apparentDemand && typeof apparentDemand === "object") {
    const demandRecord = apparentDemand as Record<string, unknown>;

    return (
      getQuantityValue(demandRecord.max) ??
      getQuantityValue(demandRecord.value) ??
      getQuantityValue(demandRecord)
    );
  }

  return getQuantityValue(record.maxDemand);
};

export const getSummaryMaxPower = (meter: SummaryMeter): number | null => {
  const record = meter as Record<string, unknown>;

  const power = record.power as Record<string, unknown> | undefined;
  const powerMax = getQuantityValue(power?.max);
  if (powerMax !== null) {
    return powerMax;
  }

  const activePower = record.activePower as Record<string, unknown> | undefined;
  const activePowerMax = getQuantityValue(activePower?.max);
  if (activePowerMax !== null) {
    return activePowerMax;
  }

  const calculated = record.calculated as Record<string, unknown> | undefined;
  const calculatedActivePower = calculated?.activePower as
    | Record<string, unknown>
    | undefined;
  const calculatedMax = getQuantityValue(calculatedActivePower?.max);
  if (calculatedMax !== null) {
    return calculatedMax;
  }

  const apparentDemand = record.apparentDemand as
    | Record<string, unknown>
    | undefined;

  return (
    getQuantityValue(apparentDemand?.max) ??
    toNumber((meter as Record<string, unknown>).maxActivePower)
  );
};

export const getSummaryAvgPower = (meter: SummaryMeter): number | null => {
  const record = meter as Record<string, unknown>;

  const power = record.power as Record<string, unknown> | undefined;
  const powerAvg = getQuantityValue(power?.avg);
  if (powerAvg !== null) {
    return powerAvg;
  }

  const activePower = record.activePower as Record<string, unknown> | undefined;
  const activePowerAvg = getQuantityValue(activePower?.avg);
  if (activePowerAvg !== null) {
    return activePowerAvg;
  }

  const calculated = record.calculated as Record<string, unknown> | undefined;
  const calculatedActivePower = calculated?.activePower as
    | Record<string, unknown>
    | undefined;
  const calculatedAvg = getQuantityValue(calculatedActivePower?.avg);
  if (calculatedAvg !== null) {
    return calculatedAvg;
  }

  const apparentDemand = record.apparentDemand as
    | Record<string, unknown>
    | undefined;

  const apparentAvg = getQuantityValue(apparentDemand?.avg);
  if (apparentAvg !== null) {
    return apparentAvg;
  }

  const totalKWh = getSummaryActiveEnergy(meter);
  const durationHours = getDurationHours(meter);

  if (
    totalKWh !== null &&
    durationHours !== null &&
    Number.isFinite(durationHours) &&
    durationHours > 0
  ) {
    return totalKWh / durationHours;
  }

  return null;
};

export const getBillingChannelValue = (
  meter: BillingMeter,
  code: string,
): number | null => {
  const record = meter as Record<string, unknown>;
  const totals = record.totals as Record<string, unknown> | undefined;
  const channels = record.channels as Record<string, unknown> | undefined;
  const calculated = record.calculated as Record<string, unknown> | undefined;

  if (channels && Object.prototype.hasOwnProperty.call(channels, code)) {
    return getQuantityValue(channels[code]) ?? 0;
  }

  const totalsValue = getQuantityValue(totals?.[code]);
  if (totalsValue !== null) {
    return totalsValue;
  }

  const calculatedKeyMap = new Map<string, string>([
    ["E", "activeEnergy"],
    ["B", "apparentEnergy"],
    ["Q", "reactiveEnergy"],
    ["K", "activeDemand"],
  ]);

  const calculatedValue = getQuantityValue(
    calculated?.[calculatedKeyMap.get(code) || code],
  );

  return calculatedValue;
};

export const formatMissingGapRange = (gap: Record<string, unknown>): string => {
  const start = formatTimePoint(gap.start);
  const end = formatTimePoint(gap.end);
  const intervalCount = gap.intervalCount;

  const cleanStart = start === "-" ? "start unknown" : start;
  const cleanEnd = end === "-" ? "end unknown" : end;

  if (typeof intervalCount === "number") {
    return `${cleanStart} to ${cleanEnd} (${intervalCount} intervals)`;
  }

  return `${cleanStart} to ${cleanEnd}`;
};

export const formatMetricLabel = (label: string): string => {
  return label
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/^./, (value) => value.toUpperCase());
};

export const formatMetricValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return "-";
  }

  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    const quantityValue = getQuantityValue(record);
    const unit = typeof record.unit === "string" ? record.unit : "";

    if (quantityValue !== null) {
      return unit ? `${quantityValue} ${unit}` : String(quantityValue);
    }

    if (record.nemTime || record.utc) {
      return formatTimePoint(record);
    }

    if (
      typeof record.actualPercentage === "number" &&
      record.qualityBreakdown &&
      typeof record.qualityBreakdown === "object"
    ) {
      const qualityBreakdown = Object.entries(
        record.qualityBreakdown as Record<string, unknown>,
      )
        .map(([key, item]) => `${key}:${item}`)
        .join(" ");

      return `${record.actualPercentage}% (${qualityBreakdown || "no flags"})`;
    }
  }

  return toDisplayValue(value);
};

export const buildMeterIdentityKey = (nmi: string, serial: string): string => {
  return `${nmi.trim().toUpperCase()}|${serial.trim().toUpperCase()}`;
};

export const getChartLineColor = (color: string, amount = 45): string => {
  const hex = color.replace("#", "");

  const r = Math.max(0, parseInt(hex.slice(0, 2), 16) - amount);
  const g = Math.max(0, parseInt(hex.slice(2, 4), 16) - amount);
  const b = Math.max(0, parseInt(hex.slice(4, 6), 16) - amount);

  return `rgb(${r}, ${g}, ${b})`;
};

export const getSummaryChartData = (
  meters: SummaryMeter[],
  primaryColor: string,
): Nem12ChartData => {
  const labels = meters.map(
    (meter) => getMeterSerial(meter) || getMeterNmi(meter),
  );
  const activeEnergy = meters.map(
    (meter) => getSummaryActiveEnergy(meter) ?? 0,
  );
  const maxDemand = meters.map((meter) => getSummaryMaxDemand(meter) ?? 0);

  return {
    labels,
    datasets: [
      {
        label: "Net kWh Total",
        data: activeEnergy,
        backgroundColor: primaryColor,
        borderColor: primaryColor,
        borderWidth: 1,
        order: 2,
        yAxisID: "y",
      },
      {
        label: "Max Demand kVA",
        data: maxDemand,
        backgroundColor: getChartLineColor(primaryColor),
        borderColor: getChartLineColor(primaryColor),
        borderWidth: 2,
        order: 1,
        type: "line" as const,
        yAxisID: "y1",
      },
    ],
  };
};

export const getBillingChartData = (
  meters: BillingMeter[],
  primaryColor: string,
  batchTotals?: BillingData["batchTotals"],
): Nem12ChartData => {
  const labels = meters.map(
    (meter) => getMeterSerial(meter) || getMeterNmi(meter),
  );

  const channelPalette = [primaryColor, "#0ea5e9", "#f59e0b", "#ef4444"];

  const channelKeys: Array<"E" | "B" | "Q" | "K"> = ["E", "B", "Q", "K"];

  const getChannelUnit = (channelCode: string): string => {
    if (!batchTotals) {
      return "";
    }

    const total = batchTotals[channelCode];
    if (!total || typeof total !== "object") {
      return "";
    }

    const unit = (total as Record<string, unknown>).unit;
    return typeof unit === "string" ? unit.trim() : "";
  };

  return {
    labels,
    datasets: channelKeys
      .map((channel, index) => {
        const rawValues = meters.map((meter) =>
          getBillingChannelValue(meter, channel),
        );
        const hasAnyData = rawValues.some((value) => value !== null);

        if (!hasAnyData) {
          return null;
        }

        return {
          label: ((): string => {
            const unit = getChannelUnit(channel);
            return unit ? `${channel} (${unit})` : channel;
          })(),
          data: rawValues.map((value) => value ?? 0),
          backgroundColor: channelPalette[index],
          borderColor: channelPalette[index],
          borderWidth: 1,
          yAxisID: "y",
        };
      })
      .filter(
        (dataset): dataset is Nem12ChartData["datasets"][number] =>
          dataset !== null,
      ),
  };
};

export const getManifestFileId = (file: Record<string, unknown>): string => {
  const id = file.id;
  const fileId = file.fileId;

  if (typeof id === "string" && id.length > 0) {
    return id;
  }

  if (typeof fileId === "string" && fileId.length > 0) {
    return fileId;
  }

  return "";
};

export const buildCommaSeparated = (values: string[]): string | undefined => {
  const normalized = values
    .map((value) => value.trim())
    .filter((value) => value.length > 0);

  return normalized.length > 0 ? normalized.join(",") : undefined;
};

export const STATUS_COLOR_CLASS: Record<string, string> = {
  valid: "completed-badge",
  invalid: "failed-badge",
  "complete with errors": "partial-data-badge",
  complete: "completed-badge",
  failed: "failed-badge",
  processing: "started-badge",
  queued: "pending-badge",
  pending: "pending-badge",
};

export const initialFilterParams: ReadingFilter = {
  dateFrom: undefined,
  dateTo: undefined,
  selectedChannels: [],
  selectedQualities: [],
  include: false,
};

export const ALL_DUPLICATE_KIND_VALUES = ["duplicate", "conflict"] as const;

export const DUPLICATE_KIND_OPTIONS: OptionValueStringType[] = [
  { value: "duplicate", label: "Duplicate" },
  { value: "conflict", label: "Conflict" },
];

export const createDefaultDuplicatesFilters = (
  pageSize: number,
): DuplicatesFilterState => ({
  nmi: "",
  serial: "",
  kind: [],
  sort: "nmi",
  order: "asc",
  pageIndex: 0,
  pageSize,
  cursorByPage: { 0: null },
  nextCursor: null,
  totalItems: 0,
});

export const createDefaultSummaryFilters = (
  pageSize: number,
): SummaryFilterState => ({
  nmi: "",
  serial: "",
  pageIndex: 0,
  pageSize,
  cursorByPage: { 0: null },
  nextCursor: null,
  totalItems: 0,
  orderBy: "nmi",
  direction: "asc",
});

export const createDefaultBillingFilters = (
  pageSize: number,
): BillingFilterState => ({
  nmi: "",
  serial: "",
  pageIndex: 0,
  pageSize,
  cursorByPage: { 0: null },
  nextCursor: null,
  totalItems: 0,
  orderBy: "nmi",
  direction: "asc",
});

export const createDefaultMissingFilters = (
  pageSize: number,
): MissingFilterState => ({
  nmi: "",
  serial: "",
  nmiSuffix: "",
  pageIndex: 0,
  pageSize,
  cursorByPage: { 0: null },
  nextCursor: null,
  totalItems: 0,
  orderBy: "nmi",
  direction: "asc",
});

export const SUMMARY_ADVANCED_CALCULATION_COLUMN_IDS = new Set([
  "powerMax",
  "powerAvg",
  "apparentDemand",
]);

export const getIncludedSuffixesDisplay = (meter: SummaryMeter): string => {
  if (!Array.isArray(meter.channels) || meter.channels.length === 0) {
    return "-";
  }

  const suffixes = meter.channels
    .map((channel) =>
      typeof channel === "string"
        ? channel.split("||").slice(-1)[0]?.trim()
        : "",
    )
    .filter((suffix): suffix is string => Boolean(suffix));

  if (suffixes.length === 0) {
    return "-";
  }

  return [...new Set(suffixes)].join(", ");
};

export const getSummaryQualityDisplay = (meter: SummaryMeter): string => {
  const qualityBreakdown = meter.dataQuality?.qualityBreakdown;

  if (!qualityBreakdown || typeof qualityBreakdown !== "object") {
    return "-";
  }

  const formatted = Object.entries(qualityBreakdown)
    .map(([qualityCode, count]) => {
      if (!qualityCode) {
        return "";
      }

      const normalizedCount =
        typeof count === "number" || typeof count === "string"
          ? String(count)
          : "";

      if (!normalizedCount) {
        return "";
      }

      return `${qualityCode}:${normalizedCount}`;
    })
    .filter((entry) => entry.length > 0);

  return formatted.length > 0 ? formatted.join(", ") : "-";
};
